import {scrollBelowHeader} from '@/utils/scrollBelowHeader.js'
import {Swiper, SwiperSlide} from 'swiper/react'
import {Pagination} from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'
import {arrowRightSm, personCarryingBag, childCarIcon, luggageBagIcon, oversizeBagIcon, editPenIcon} from '@img/index.js'

// Collapsible summary cards for Passengers / Child Seats / Luggage / Oversized.
// Title + icon live inside the card; a summary sub-title shows when collapsed; expanding
// reveals the item listing (desktop vertical scroll, mobile horizontal swiper) — mirroring
// the order-details page. The edit button appears only when expanded; policies disable it
// (they never block expanding).

const ChevronIcon = ({expanded}) => (
    <img src={arrowRightSm} alt=""
         className="w-4.5 h-4.5 transition-transform duration-300"
         style={{transform: `rotate(${expanded ? -90 : 90}deg)`}}/>
)

const EditButton = ({onClick, policy = {canEdit: true, blockedReason: null}}) => (
    <div className="relative shrink-0 group">
        <button type="button" disabled={!policy.canEdit} onClick={onClick}
                className="cursor-pointer w-10 h-10 shrink-0 rounded-full bg-(--text-primary) flex items-center justify-center p-0 border-0 disabled:opacity-50 disabled:cursor-not-allowed">
            <img src={editPenIcon} alt="Edit"/>
        </button>
        {!policy.canEdit && policy.blockedReason && (
            <div className="hidden group-hover:block absolute top-full right-0 mt-3 bg-[#3c393c] text-white rounded-xl p-3 text-center text-sm z-10 w-max max-w-[280px] after:content-[''] after:absolute after:w-0 after:h-0 after:border-l-24 after:border-l-transparent after:border-r-[9px] after:border-r-transparent after:border-b-20 after:border-b-[#3c393c] after:-top-4.75 after:right-7.5">
                <p className="mb-0!">{policy.blockedReason}</p>
            </div>
        )}
    </div>
)

// `readOnly` = the view page: the listing still expands, the pencil is simply absent
// (not a disabled one, which would read as "you're blocked"). See RouteCard.
function CollapsibleSummaryCard({title, icon, subtitle, items, emptyLabel, expanded, onToggle, onEdit, policy, readOnly = false}) {
    return (
        <div className="bg-white px-4 py-1 rounded-(--radius-xl) border border-(--text-primary)">
            <div className="cursor-pointer flex items-center gap-5 min-h-[64px]" onClick={(e) => {
                onToggle()
                // Expanding brings the whole card to the top of the screen, so its list
                // never opens half below the fold on a phone.
                if (!expanded) {
                    const card = e.currentTarget.parentElement
                    setTimeout(() => scrollBelowHeader(card), 50)
                }
            }}>
                {icon && <img src={icon} alt="" className="max-h-9 shrink-0"/>}
                <div className="flex-1 min-w-0">
                    <h2 className="font-bold text-xl mb-1">{title}</h2>
                    {!expanded && subtitle && (
                        <span className="block text-sm text-(--text-secondary)">{subtitle}</span>
                    )}
                </div>
                <span className="flex items-center shrink-0">
                    <ChevronIcon expanded={expanded}/>
                </span>
            </div>
            {expanded && (
                <div className="pt-4 pb-3">
                    {/* Desktop list */}
                    <div className="hidden md:block bg-(--bg-grey) rounded-xl p-4 max-h-55 overflow-y-auto">
                        {items.length > 0 ? items.map((item, i) => (
                            <div key={i} className="flex items-center gap-4 pb-4 mb-4 border-b border-white last:border-0 last:pb-0 last:mb-0">
                                {(item.icon_url || item.image_url) && (
                                    <img src={item.icon_url || item.image_url} alt="" className="w-9 h-9 object-contain shrink-0"/>
                                )}
                                <span className="text-sm">{item.short_label || item.label}: {item.quantity}</span>
                            </div>
                        )) : <p className="mb-0 text-sm text-(--text-secondary)">{emptyLabel}</p>}
                    </div>
                    {/* Mobile swiper */}
                    <div className="md:hidden edit-swiper-pagination pagination-below">
                        {items.length > 0 ? (
                            <Swiper modules={[Pagination]} spaceBetween={12} pagination={{clickable: true}}
                                    breakpoints={{0: {slidesPerView: 1.5}, 425: {slidesPerView: 2.5}}}>
                                {items.map((item, i) => (
                                    <SwiperSlide key={i}>
                                        <div className="p-3 rounded-3xl shadow-[0px_1.74px_13.93px_rgba(157,154,154,0.2)]">
                                            {(item.icon_url || item.image_url) && (
                                                <img src={item.icon_url || item.image_url} alt="" className="w-full h-16 object-contain mb-2"/>
                                            )}
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-sm font-semibold min-w-0 truncate">{item.short_label || item.label}</span>
                                                <span className="text-sm font-semibold shrink-0">{item.quantity}</span>
                                            </div>
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        ) : (
                            <p className="mb-0 text-sm text-(--text-secondary)">{emptyLabel}</p>
                        )}
                    </div>
                    {!readOnly && (
                        <div className="flex items-end justify-end mt-4">
                            <EditButton onClick={onEdit} policy={policy}/>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export function PassengersCard({trip, expanded, onToggle, onEdit, policy, readOnly}) {
    const items = (trip?.selected_passengers || []).filter(p => (parseInt(p.quantity) || 0) > 0)
    const subtitle = items.map(p => `${p.label}: ${p.quantity}`).join(' · ')
    return (
        <CollapsibleSummaryCard title="Passengers" icon={personCarryingBag} subtitle={subtitle}
                                items={items} emptyLabel="No passengers selected"
                                expanded={expanded} onToggle={onToggle} onEdit={onEdit} policy={policy} readOnly={readOnly}/>
    )
}

export function ChildSeatsCard({trip, expanded, onToggle, onEdit, policy, readOnly}) {
    const items = (trip?.selected_child_seats || []).filter(s => (parseInt(s.quantity) || 0) > 0)
    const subtitle = items.map(s => `${s.label}: ${s.quantity}`).join(' · ')
    return (
        <CollapsibleSummaryCard title="Child Seats" icon={childCarIcon} subtitle={subtitle}
                                items={items} emptyLabel="No child seats selected"
                                expanded={expanded} onToggle={onToggle} onEdit={onEdit} policy={policy} readOnly={readOnly}/>
    )
}

export function LuggageItemsCard({trip, expanded, onToggle, onEdit, policy, readOnly}) {
    const items = (trip?.selected_luggage || []).filter(l => l.category === 'Standard' && (parseInt(l.quantity) || 0) > 0)
    const subtitle = items.map(l => `${l.quantity}×${l.short_label || l.label}`).join(' · ')
    return (
        <CollapsibleSummaryCard title="Luggage" icon={luggageBagIcon} subtitle={subtitle}
                                items={items} emptyLabel="No luggage selected"
                                expanded={expanded} onToggle={onToggle} onEdit={onEdit} policy={policy} readOnly={readOnly}/>
    )
}

export function OversizedCard({trip, expanded, onToggle, onEdit, policy, readOnly}) {
    const items = (trip?.selected_luggage || []).filter(l => l.category === 'Oversized' && (parseInt(l.quantity) || 0) > 0)
    const subtitle = items.map(l => `${l.label}: ${l.quantity}`).join(' · ')
    return (
        <CollapsibleSummaryCard title="Oversized" icon={oversizeBagIcon} subtitle={subtitle}
                                items={items} emptyLabel="No oversized items selected"
                                expanded={expanded} onToggle={onToggle} onEdit={onEdit} policy={policy} readOnly={readOnly}/>
    )
}
